/**
 * eReg Upload Page
 *
 * Document upload with AI-powered metadata suggestions.
 */

import { useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  Upload as UploadIcon,
  FileText,
  X,
  Check,
  Sparkles,
  AlertCircle,
  ArrowLeft,
} from 'lucide-react';
import { useEreg } from '../contexts/EregContext';
import * as eregApi from '../lib/api';
import { PageHeader, fieldCls, ConfirmModal, useToast } from '../../ui';
import { sanitizeReturnTo, appendQueryParam } from '../../ui/returnTo';
import { STUDY_ROLE_LABELS, ROLE_TO_DOCUMENT_TYPE } from '../../ui/studyRoles';

export function Upload() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { currentFolder, folders, documentTypes, currentProtocol, siteMode, refreshFolders } = useEreg();
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Deep-link params (plan §3.6): a preselected type from ?document_type=, or
  // ?role= mapped to a type — the /ereg/studies/:id/upload?role=… contract
  // (the mapping lived in StudyDeepLink before the URL-canonical routes).
  const returnTo = sanitizeReturnTo(searchParams.get('return_to'));
  const returnLabel = searchParams.get('return_label') || 'previous page';
  const roleParam = searchParams.get('role');
  const presetType =
    searchParams.get('document_type') || (roleParam && ROLE_TO_DOCUMENT_TYPE[roleParam]) || '';

  // Canonical folders URL for this scope (study / site zone / legacy fallback).
  const foldersPath = currentProtocol
    ? `/ereg/studies/${currentProtocol.id}/folders`
    : siteMode
      ? '/ereg/site/folders'
      : '/ereg/folders';

  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [suggestingMetadata, setSuggestingMetadata] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // §3.2: uploading a type whose default slot is already occupied prompts
  // "Make this the current <Role>?" before leaving the page.
  const [pinPrompt, setPinPrompt] = useState<{ role: string; documentId: string } | null>(null);
  const [pinning, setPinning] = useState(false);

  const [formData, setFormData] = useState({
    folder_id: currentFolder?.id || '',
    document_name: '',
    document_type: presetType,
    document_date: '',
    version_number: '1.0',
    expiration_date: '',
    description: '',
  });

  const handleFileDrop = useCallback(async (e: React.DragEvent) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      await handleFileSelect(droppedFile);
    }
  }, []);

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError(null);
    // Reset the native input so removing a file and re-picking the SAME file
    // still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Extract filename as default document name
    const nameWithoutExt = selectedFile.name.replace(/\.[^.]+$/, '');
    setFormData(prev => ({
      ...prev,
      document_name: nameWithoutExt,
    }));

    // Try AI metadata suggestion for PDFs
    if (selectedFile.type === 'application/pdf') {
      setSuggestingMetadata(true);
      try {
        const suggestions = await eregApi.suggestMetadata(selectedFile);
        setFormData(prev => ({
          ...prev,
          document_name: suggestions.document_name || prev.document_name,
          document_type: suggestions.document_type || prev.document_type,
        }));
      } catch (err) {
        console.error('Error getting metadata suggestions:', err);
      } finally {
        setSuggestingMetadata(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file || !formData.folder_id || !formData.document_name || !formData.document_type) {
      setError('Please fill in all required fields');
      return;
    }

    setUploading(true);
    setError(null);

    try {
      const uploaded: any = await eregApi.uploadDocument(file, {
        folder_id: formData.folder_id,
        document_name: formData.document_name,
        document_type: formData.document_type,
        document_date: formData.document_date || undefined,
        version_number: formData.version_number,
        expiration_date: formData.expiration_date || undefined,
        description: formData.description || undefined,
      });

      // Refresh folders to update document counts
      await refreshFolders();

      // Occupied key-document slot → ask before leaving (§3.2). An empty slot
      // was auto-pinned server-side; no prompt needed.
      const keyDoc = uploaded?.key_document;
      if (keyDoc?.occupied_by_document_id && keyDoc?.role && currentProtocol) {
        setPinPrompt({ role: keyDoc.role, documentId: uploaded.id });
        return;
      }

      toast('Document uploaded');
      leaveAfterUpload();
    } catch (err: any) {
      console.error('Error uploading document:', err);
      setError(err?.response?.data?.error || 'Failed to upload document. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // return_to convention (§3.6): deep-linked uploads go back to the caller
  // with ?uploaded=1; in-app uploads return to folders.
  const leaveAfterUpload = () => {
    if (returnTo) {
      navigate(appendQueryParam(returnTo, 'uploaded', '1'));
    } else {
      navigate(foldersPath);
    }
  };

  const confirmRepin = async () => {
    if (!pinPrompt || !currentProtocol) return;
    setPinning(true);
    try {
      await eregApi.setKeyDocument(currentProtocol.id, pinPrompt.role, { document_id: pinPrompt.documentId });
      toast('Pinned as the current version');
    } catch (err: any) {
      console.error('Failed to repin key document:', err);
      // The upload itself succeeded — but the OLD document is still the pinned
      // one, and every consumer tool keeps reading it. Say so.
      toast(
        err?.response?.data?.error ||
          'Pin failed — the previous document is still the current one. Re-pin it from Key Documents.',
        { type: 'error', duration: 8000 }
      );
    } finally {
      setPinning(false);
      setPinPrompt(null);
      leaveAfterUpload();
    }
  };

  if (!currentProtocol && !siteMode) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center dark:bg-gray-800 dark:border-gray-700">
          <UploadIcon size={48} className="mx-auto mb-4 text-gray-300 dark:text-gray-600" />
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No Study Selected</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            Select a study from the header dropdown to upload documents.
          </p>
          <Link
            to="/ereg/dashboard"
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 transition-colors duration-150 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
          >
            Pick a study on the Dashboard
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {returnTo && (
        <Link
          to={returnTo}
          className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-700 transition-colors duration-150 hover:text-primary-800 dark:text-primary-400 dark:hover:text-primary-300"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to {returnLabel}
        </Link>
      )}

      {/* Header */}
      <PageHeader
        title="Upload Document"
        subtitle={siteMode ? 'Upload to Site Documents' : `Upload to ${currentProtocol?.protocol_number}`}
      />

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* File Drop Zone */}
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={handleFileDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-colors duration-150 ${
            file ? 'border-primary-300 bg-primary-50 dark:border-primary-800 dark:bg-primary-900/20' : 'border-gray-300 hover:border-primary-400 dark:border-gray-600 dark:hover:border-primary-500'
          }`}
        >
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={24} className="text-primary-600 dark:text-primary-400" />
              <div className="text-left">
                <p className="font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
              <button
                type="button"
                onClick={() => setFile(null)}
                className="p-1 hover:bg-primary-100 rounded transition-colors duration-150 dark:hover:bg-primary-900/40"
              >
                <X size={18} className="text-gray-500 dark:text-gray-400" />
              </button>
            </div>
          ) : (
            <>
              <UploadIcon size={40} className="mx-auto mb-3 text-gray-400 dark:text-gray-500" />
              <p className="text-gray-600 dark:text-gray-400 mb-2">
                Drag and drop a file here, or{' '}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                >
                  browse
                </button>
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                PDF, Word, Excel, PowerPoint (.pptx), images, CSV, text (max 50MB)
              </p>
            </>
          )}
          {/* accept list mirrors the backend ALLOWED_TYPES — legacy .ppt is
              rejected server-side, so don't offer it here. */}
          <input
            ref={fileInputRef}
            type="file"
            onChange={(e) => e.target.files?.[0] && handleFileSelect(e.target.files[0])}
            className="hidden"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.pptx,.png,.jpg,.jpeg,.gif,.csv,.txt"
          />
        </div>

        {/* AI Suggestion Loading */}
        {suggestingMetadata && (
          <div className="flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400">
            <Sparkles size={16} className="animate-pulse" />
            <span>Analyzing document for metadata suggestions...</span>
          </div>
        )}

        {/* Metadata Form */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-4 dark:bg-gray-800 dark:border-gray-700">
          <h3 className="font-medium text-gray-900 dark:text-gray-100">Document Details</h3>

          {/* Folder */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Folder *
            </label>
            <select
              value={formData.folder_id}
              onChange={(e) => setFormData({ ...formData, folder_id: e.target.value })}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select folder...</option>
              {folders.map((folder) => (
                <option key={folder.id} value={folder.id}>
                  {folder.path}
                </option>
              ))}
            </select>
            {folders.length === 0 && (
              <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
                This {siteMode ? 'site' : 'study'} has no folders yet —{' '}
                <Link to={foldersPath} className="font-medium underline">
                  set up folders
                </Link>{' '}
                first.
              </p>
            )}
          </div>

          {/* Document Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Name *
              {formData.document_name && !suggestingMetadata && (
                <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">
                  <Sparkles size={12} className="inline" /> AI suggested
                </span>
              )}
            </label>
            <input
              type="text"
              value={formData.document_name}
              onChange={(e) => setFormData({ ...formData, document_name: e.target.value })}
              className={`w-full ${fieldCls}`}
              required
            />
          </div>

          {/* Document Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Document Type *
              {formData.document_type && !suggestingMetadata && (
                <span className="ml-2 text-xs text-primary-600 dark:text-primary-400">
                  <Sparkles size={12} className="inline" /> AI suggested
                </span>
              )}
            </label>
            <select
              value={formData.document_type}
              onChange={(e) => setFormData({ ...formData, document_type: e.target.value })}
              className={`w-full ${fieldCls}`}
              required
            >
              <option value="">Select type...</option>
              {documentTypes.map((type) => (
                <option key={type.id} value={type.name}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          {/* Version and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Version
              </label>
              <input
                type="text"
                value={formData.version_number}
                onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Document Date
              </label>
              <input
                type="date"
                value={formData.document_date}
                onChange={(e) => setFormData({ ...formData, document_date: e.target.value })}
                className={`w-full ${fieldCls}`}
              />
            </div>
          </div>

          {/* Expiration Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Expiration Date
            </label>
            <input
              type="date"
              value={formData.expiration_date}
              onChange={(e) => setFormData({ ...formData, expiration_date: e.target.value })}
              className={`w-full ${fieldCls}`}
            />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description / Notes
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
              className={`w-full ${fieldCls}`}
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="flex items-center gap-2 text-red-600 dark:text-red-400 text-sm">
            <AlertCircle size={16} />
            <span>{error}</span>
          </div>
        )}

        {/* Submit Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={() => navigate(returnTo || foldersPath)}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors duration-150 dark:text-gray-300 dark:hover:bg-gray-700"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploading || !file}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 disabled:opacity-50"
          >
            {uploading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Uploading...
              </>
            ) : (
              <>
                <Check size={18} />
                Upload Document
              </>
            )}
          </button>
        </div>
      </form>

      {/* Occupied key-document slot prompt (§3.2) — outside the form so the
          modal's buttons can never resubmit the upload. */}
      <ConfirmModal
        isOpen={!!pinPrompt}
        onClose={() => { setPinPrompt(null); leaveAfterUpload(); }}
        onConfirm={confirmRepin}
        title={`Make this the current ${pinPrompt ? (STUDY_ROLE_LABELS[pinPrompt.role] || pinPrompt.role) : ''}?`}
        message="Another document currently fills this key-document slot. Pin the new upload so every tool reads it as the current version — or keep it filed without pinning."
        confirmLabel="Pin as current"
        cancelLabel="Just file it"
        loading={pinning}
      />
    </div>
  );
}
